import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { formatPrice } from "@/lib/api";
import { 
  TrendingUp, 
  DollarSign, 
  Package, 
  AlertTriangle,
  CheckCircle,
  Calendar,
  BarChart3
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function SalesStatsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/scooters/sales-stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load sales stats", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-secondary rounded w-1/3"></div>
            <div className="h-8 bg-secondary rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <Card className="border-border border-green-500/30" data-testid="sales-stats-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <BarChart3 className="w-5 h-5 text-green-500" />
          Statistiques Ventes / Statistici Vânzări
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 bg-secondary/50 rounded-lg text-center">
            <Package className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
            <p className="text-2xl font-mono font-bold">{stats.total_sold}</p>
            <p className="text-xs text-muted-foreground">Vendus / Vânduți</p>
          </div>
          
          <div className="p-3 bg-green-500/10 rounded-lg text-center">
            <DollarSign className="w-5 h-5 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-mono font-bold text-green-500">{formatPrice(stats.total_revenue)}</p>
            <p className="text-xs text-muted-foreground">Revenus / Venituri</p>
          </div>
          
          <div className="p-3 bg-blue-500/10 rounded-lg text-center">
            <TrendingUp className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <p className={`text-2xl font-mono font-bold ${stats.total_profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {formatPrice(stats.total_profit)}
            </p>
            <p className="text-xs text-muted-foreground">Profit total</p>
          </div>
          
          <div className="p-3 bg-purple-500/10 rounded-lg text-center">
            <Calendar className="w-5 h-5 mx-auto mb-1 text-purple-500" />
            <p className="text-2xl font-mono font-bold text-purple-500">{stats.recent_sales_count}</p>
            <p className="text-xs text-muted-foreground">30 derniers jours</p>
          </div>
        </div>

        {/* Average Profit */}
        <div className="p-3 bg-secondary/30 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Profit moyen par scooter:</span>
            <span className="font-mono font-bold text-primary">
              {formatPrice(stats.avg_profit_per_scooter)}
            </span>
          </div>
        </div>

        {/* Recent Sales */}
        {stats.recent_sales?.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">
                Dernières ventes / Ultimele vânzări
              </h4>
              <div className="space-y-2">
                {stats.recent_sales.slice(0, 5).map((sale, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm p-2 bg-secondary/30 rounded">
                    <span className="font-medium">{sale.brand} {sale.model}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-xs">
                        {sale.scooter_id}
                      </Badge>
                      <span className="font-mono text-green-500">
                        {formatPrice(sale.sale_record?.sale_price || sale.selling_price)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function ServiceAlertsWidget() {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/scooters/service-alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error("Failed to load service alerts", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-secondary rounded w-1/3"></div>
            <div className="h-8 bg-secondary rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!alerts || alerts.total_pending === 0) {
    return (
      <Card className="border-border border-green-500/30" data-testid="service-alerts-widget">
        <CardContent className="p-6 text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
          <p className="font-medium">Aucune alerte en attente</p>
          <p className="text-sm text-muted-foreground">Tous les services sont à jour!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-border ${alerts.overdue_count > 0 ? 'border-red-500/50' : 'border-yellow-500/30'}`} data-testid="service-alerts-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertTriangle className={`w-5 h-5 ${alerts.overdue_count > 0 ? 'text-red-500' : 'text-yellow-500'}`} />
          Alertes Service
          {alerts.overdue_count > 0 && (
            <Badge className="bg-red-500 text-white animate-pulse">
              {alerts.overdue_count} en retard!
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-4 text-center">
          <div className="flex-1 p-2 bg-yellow-500/10 rounded">
            <p className="text-2xl font-mono font-bold text-yellow-500">{alerts.total_pending}</p>
            <p className="text-xs text-muted-foreground">En attente</p>
          </div>
          <div className="flex-1 p-2 bg-red-500/10 rounded">
            <p className="text-2xl font-mono font-bold text-red-500">{alerts.overdue_count}</p>
            <p className="text-xs text-muted-foreground">En retard</p>
          </div>
        </div>

        {/* Alert List */}
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {alerts.alerts.slice(0, 5).map((alert, idx) => (
            <div 
              key={idx} 
              className={`p-2 rounded text-sm ${alert.is_overdue || alert.mileage_exceeded ? 'bg-red-500/10' : 'bg-secondary/50'}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{alert.scooter_name}</span>
                <Badge variant="outline" className="text-xs font-mono">
                  {alert.scooter_id}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {alert.alert_type.replace('_', ' ')} 
                {alert.due_date && ` - ${alert.due_date}`}
                {alert.due_mileage > 0 && ` - ${alert.due_mileage.toLocaleString()}km`}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default { SalesStatsWidget, ServiceAlertsWidget };
